import React, { useState } from 'react';
import { Formik, Form as FormikForm, useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import { Field } from '@dashboard/components/Formik/Form';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { getIsFieldHidden } from '@dashboard/components/Formik/formUtils';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import PriceForm from '../PricesList/PriceForm';
import { normalizeRevealFlags } from '../PricesList/priceHelpers';
import Images from '@dashboard/components/Formik/components/Images';
import LexicalRichTextEditor from '@dashboard/components/Formik/components/LexicalRichTextEditor';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import useMountEffect from '@dashboard/hooks/useMountEffect';

import BumpConditionalLogicField, {
  ORDER_BUMP_SUBJECT,
  getSelectionSubject,
} from './BumpConditionalLogicField';
import { getRuleLabels } from './describeBumpRule';
import { ORDER_BUMP_COPY } from './offerCopy';
import FeaturesField, { newFeature } from './FeaturesField';
import HighlightField from './HighlightField';
import ProductFilesForm, { FilesEditor } from '../ProductFilesForm';
import ProductMediaForm from '../ProductMediaForm';
import { getProductFormValues } from '../ProductDetailsForm';
import draftToPricePayload from '../../lib/draftToPricePayload';
import useProduct from '../../useProduct';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

// A bump must share the page product's currency or resolveOrderBumpOffers drops
// the placement — on every page offering it, with no error where the edit
// happened. There is nothing to choose, so the code prints inside the amount
// field and the tooltip on it says why.
const currencyTooltip = copy =>
  `An ${copy.noun} must use the same currency as the ${copy.currencyOwner}.`;

const NO_SHOW_HIDE_LOGIC = {
  enabled: false,
  comparison: 'IS',
  priceIds: [],
  ticketTypeIds: [],
  orderBumpProductIds: [],
};

// A rule reaches this form from three places: an entry saved before the field
// existed (absent), one the API filled in (complete), and Formik's own partial
// writes. So it is flattened before it is compared or sent. A rule saved before
// bumps could be watched has no subject, which is the page's own selection.
const normalizeShowHideLogic = (logic, lists) => {
  const subject =
    logic?.subject === ORDER_BUMP_SUBJECT
      ? ORDER_BUMP_SUBJECT
      : getSelectionSubject(lists.ticketTypes);
  const normalized = {
    enabled: logic?.enabled === true,
    subject,
    comparison: logic?.comparison || 'IS',
    priceIds: (logic?.priceIds ?? []).map(String),
    ticketTypeIds: (logic?.ticketTypeIds ?? []).map(String),
    orderBumpProductIds: (logic?.orderBumpProductIds ?? []).map(String),
  };

  return { ...normalized, labels: getRuleLabels(normalized, logic?.labels, lists) };
};

// Only the settings that differ from what the page holds are sent, so an
// ordinary Save does not touch the placement, and a new bump attaches with
// nothing but what the seller ticked.
const placementPatch = (next, saved) =>
  Object.fromEntries(Object.entries(next).filter(([key, value]) => !isEqual(value, saved[key])));

const BUMP_IMAGE_LIMIT = 1;

// A new bump opens with three lines to fill rather than an Add button to find;
// left blank they are dropped on save, so a saved list with fewer than three
// comes back padded to three the same way.
const DEFAULT_FEATURE_ROWS = 3;

const seedFeatures = features => {
  const rows = (Array.isArray(features) ? features : []).map(newFeature);
  while (rows.length < DEFAULT_FEATURE_ROWS) rows.push(newFeature());
  return rows;
};

// Blank rows are the ones the seller never filled, not features; the row key
// is the form's own. An empty list is sent as one so the API clears it.
const toFeaturesPayload = rows =>
  (rows ?? [])
    .filter(row => row.text?.trim())
    .map(row => ({ text: row.text.trim(), icon: row.icon || null }));

// The card renders both of these untruncated beside a thumbnail and a checkbox,
// so a long one wraps the bump out of shape. 120 holds each to about two lines
// at checkout width while still allowing a real sentence.
const HEADING_LIMIT = 120;
const CALL_TO_ACTION_LIMIT = 120;
const SHORT_DESCRIPTION_LIMIT = 120;

// maxLength stops new typing but cannot shorten a value saved before the limit
// existed, so the count turns red rather than silently reading wrong.
function CharacterCount({ used, limit }) {
  return (
    <span className={`block text-right ${used > limit ? 'text-red-600' : ''}`}>
      {used}/{limit}
    </span>
  );
}

/**
 * The create payload for a brand-new order bump product. The price goes through
 * the same draft-to-payload conversion the product form uses, so a bump created
 * here is shaped exactly like one edited later.
 */
export function buildNewBumpProduct({
  title,
  headingText,
  callToActionText,
  shortDescription,
  features,
  calloutText,
  calloutIcon,
  description,
  currency,
  price,
  hasUnlimitedStock,
  stock,
  sku,
}) {
  const converted = draftToPricePayload(price, currency);
  const featureLines = toFeaturesPayload(features);

  return {
    title: title.trim(),
    headingText: headingText?.trim() || undefined,
    callToActionText: callToActionText.trim() || undefined,
    shortDescription: shortDescription.trim() || undefined,
    features: featureLines.length ? featureLines : undefined,
    calloutText: calloutText?.trim() || undefined,
    // No box without text, so the icon goes with it.
    calloutIcon: calloutText?.trim() ? calloutIcon || undefined : undefined,
    // Plain scalars, so unlike images and downloads they ride the create itself.
    hasUnlimitedStock,
    stock: stock || undefined,
    sku: sku?.trim() || undefined,
    // Lexical JSON — the API derives descriptionHtml from it on save.
    description,
    currency,
    // Only a product created AS an order bump may be placed as one, and the
    // role is immutable afterwards.
    role: 'orderbump',
    // Legacy product scalar, denormalized from the single price it is created with.
    type: converted.billingType === 'recurring' ? 'subscription' : 'charge',
    prices: [{ ...converted, enabled: true, order: 0 }],
  };
}

const identityFields = [
  {
    name: 'title',
    label: 'Product title',
    type: 'text',
    required: true,
    placeholder: 'Product title',
  },
  {
    name: 'shortDescription',
    label: 'Short description',
    type: 'text',
    maxLength: SHORT_DESCRIPTION_LIMIT,
    placeholder: 'One-line description',
    helpText: values => (
      <CharacterCount
        used={(values.shortDescription ?? '').length}
        limit={SHORT_DESCRIPTION_LIMIT}
      />
    ),
  },
  // PriceForm's Badge field pulled out of the price block (see its `hideBadge`
  // prop) so it sits with the card's identity copy, with the icon picker the
  // price block does not offer.
  {
    name: 'prices.0.badge',
    label: 'Highlight',
    type: 'custom',
    tooltip: 'A badge that highlights this price option to customers, with an optional icon.',
    renderField: () => <HighlightField placeholder="e.g. Most popular, 20% OFF" />,
  },
  { type: 'divider' },
];

// The card's own copy, written after the seller has settled what the bump is,
// what it costs and what it looks like.
const cardCopyFields = [
  {
    name: 'headingText',
    label: 'Heading',
    type: 'text',
    maxLength: HEADING_LIMIT,
    placeholder: 'e.g. Special one time offer',
    tooltip: 'A heading shown above the card.',
  },
  {
    name: 'callToActionText',
    label: 'Call to action',
    type: 'text',
    // Capped but uncounted: real accept copy is a handful of words, so a live
    // count would be a number that never moves.
    maxLength: CALL_TO_ACTION_LIMIT,
    placeholder: 'e.g. Yes! Add to my order',
    tooltip: 'The call to action shown on the card.',
  },
];

// Sections are joined with a rule only between the ones that survive, so a
// section create mode drops cannot strand a divider at the top of the panel.
const joinSections = sections =>
  sections
    .filter(section => section.length > 0)
    .flatMap((section, index) =>
      index === 0 ? section : [{ type: 'divider', name: `section-divider-${index}` }, ...section],
    );

// Placement state, so it belongs to the page rather than the product — and
// unlike the image and downloads it rides Save rather than committing as it is
// edited, so the preview only moves once the seller says so (see the submit
// handler). Creating, it rides the attach that places the bump.
const optionsFields = ({
  values,
  setFieldValue,
  primaryPrices,
  ticketTypes,
  otherOrderBumps,
  copy,
}) => [
  {
    name: 'preselected',
    label: 'Preselect',
    type: 'checkbox',
    tooltip: `Selected by default when the ${copy.noun} is shown. Customers can deselect it.`,
    reduceMargin: true,
  },
  {
    name: 'allowQuantity',
    label: 'Allow quantity',
    type: 'checkbox',
    tooltip: `Show a quantity selector so customers can add more than one of this ${copy.noun}.`,
    reduceMargin: true,
  },
  ...conditionalLogicFields({
    values,
    setFieldValue,
    primaryPrices,
    ticketTypes,
    otherOrderBumps,
    copy,
  }),
  {
    name: 'hidden',
    label: 'Hide',
    type: 'checkbox',
    // Distinct from Disable (`enabled`): a hidden bump is removed from the
    // checkout but can be revealed by listing the price reference below in
    // the `show_order_bumps` query param.
    tooltip: (
      <>
        Hide this {copy.noun} and show it with query parameters.{' '}
        <Link
          to="/docs/articles/reveal-and-hide-variants-using-query-parameters"
          target="_blank"
          variant="light"
        >
          Learn more
        </Link>
      </>
    ),
  },
];

// The rule watches the page product's price choice on a checkout, the tickets in
// the basket on an event, or on either the page's other bumps.
const conditionalLogicFields = ({
  values,
  setFieldValue,
  primaryPrices,
  ticketTypes,
  otherOrderBumps,
  copy,
}) => [
  {
    name: 'showHideLogic.enabled',
    label: 'Conditional logic',
    type: 'checkbox',
    tooltip: (
      <>
        Show this {copy.noun} based on {ticketTypes ? 'the tickets' : 'the price'} or other{' '}
        {copy.plural} selected.{' '}
        <Link
          to="/docs/articles/conditional-logic-for-product-variants"
          target="_blank"
          variant="light"
        >
          Learn more
        </Link>
      </>
    ),
    reduceMargin: true,
    // Unticking CLEARS the rule rather than parking it. Parked, reticking
    // came back with the previously chosen prices already ticked, which
    // reads as the field defaulting to prices nobody picked.
    onChange: value => {
      if (!value) setFieldValue('showHideLogic', NO_SHOW_HIDE_LOGIC);
    },
  },
  ...(values.showHideLogic?.enabled
    ? [
        {
          name: 'showHideLogic',
          type: 'custom',
          inset: true,
          renderField: () => (
            <BumpConditionalLogicField
              prices={primaryPrices}
              ticketTypes={ticketTypes}
              otherOrderBumps={otherOrderBumps}
              copy={copy}
            />
          ),
        },
      ]
    : []),
];

const inventoryFields = values => [
  { type: 'heading', level: 4, label: 'Inventory', name: 'inventory-heading' },
  { name: 'hasUnlimitedStock', label: 'Unlimited stock', type: 'toggle' },
  {
    // Two up, so SKU keeps to half the row when unlimited stock hides Stock —
    // and sits beside it rather than under it when it does not.
    name: 'inventory-columns',
    type: 'columns',
    columns: 2,
    fields: [
      ...(values.hasUnlimitedStock
        ? []
        : [{ name: 'stock', label: 'Stock', type: 'text', placeholder: '0' }]),
      { name: 'sku', label: 'SKU', type: 'text', placeholder: '1004' },
    ],
  },
];

// The price's reference, which PriceForm keeps in a disclosure it hides for a
// lone price. Saved bumps only, as there: the server derives one on save
// (ProductService.withPriceReference), so there is nothing to show until then.
// It belongs to the PRICE rather than the stock count, so it stands alone
// rather than closing out Inventory.
const referenceFields = [
  {
    name: 'prices[0].reference',
    label: 'Reference',
    type: 'text',
    className: 'font-mono text-[13px]',
    placeholder: 'price_one_time_fixed_1999',
    helpText: 'Internal id used in exports and reports.',
  },
];

/**
 * Everything the bump card does not lead with, behind one disclosure.
 *
 * The split is what the buyer sees first: title, short description, price,
 * accept copy, image and downloads build the card and stay on screen. Stock and
 * the placement's own options never appear on the card at all — so they sit here
 * rather than making the add form a scroll.
 */
const additionalFields = ({
  isCreate,
  primaryPrices,
  copy,
  ticketTypes,
  otherOrderBumps,
  values,
  setFieldValue,
}) =>
  joinSections([
    optionsFields({
      values,
      setFieldValue,
      primaryPrices,
      ticketTypes,
      otherOrderBumps,
      copy,
    }),
    inventoryFields(values),
    isCreate ? [] : referenceFields,
  ]);

// Before the product exists there is nothing to attach media to, so a create-mode
// upload parks its file id in form state and the caller attaches it once the
// create returns. The FILE is already stored by then — only the product link is
// deferred — so `media` stays empty and Images keeps rendering its own upload,
// which is what carries the preview URL.
const NO_MEDIA = [];

/**
 * The description editor is the expensive thing in this form — Lexical with a
 * dozen plugins, one of which pulls in CodeMirror. It used to sit inside a
 * disclosure, which Radix leaves unmounted until opened; now that it is on
 * screen from the start, this one-tick defer is what keeps OPENING A ROW cheap:
 * the form paints against a placeholder and the editor is built a frame later.
 */
function DeferredDescription({ onLoading }) {
  const [isReady, setIsReady] = useState(false);

  useMountEffect(() => {
    const timer = window.setTimeout(() => setIsReady(true), 0);
    return () => window.clearTimeout(timer);
  });

  if (!isReady) return <Skeleton className="h-40 w-full" />;

  return (
    <LexicalRichTextEditor
      name="description"
      id="description"
      placeholder="Describe what you’re selling"
      onLoading={onLoading}
      // The editor sits well down the form, and focusing it scrolls it into
      // view — so left to autofocus it drags the row to itself as it mounts,
      // stealing whatever the seller had just clicked.
      autoFocus={false}
    />
  );
}

// Same deferral for downloads: the upload stands alone, only the attach needs a
// product. Holding plain ids is enough — FilesEditor already resolves a bare id
// against the records it saw upload, which is how it survives a reorder.
function DraftFilesForm() {
  const { values, setFieldValue } = useFormikContext();

  return (
    <FilesEditor
      files={values.fileIds}
      onAttach={fileId => setFieldValue('fileIds', [...values.fileIds, String(fileId)])}
      onDetach={fileId =>
        setFieldValue(
          'fileIds',
          values.fileIds.filter(id => id !== String(fileId)),
        )
      }
      onReorder={fileIds => setFieldValue('fileIds', fileIds.map(String))}
      onEditFile={() => {}}
    />
  );
}

function DraftMediaForm() {
  const { values, setFieldValue } = useFormikContext();

  return (
    <Images
      media={NO_MEDIA}
      maxItems={BUMP_IMAGE_LIMIT}
      onAttach={fileId => setFieldValue('mediaFileIds', [...values.mediaFileIds, String(fileId)])}
      onDetach={fileId =>
        setFieldValue(
          'mediaFileIds',
          values.mediaFileIds.filter(id => id !== String(fileId)),
        )
      }
      onReorder={() => {}}
    />
  );
}

function FormBody({
  productId,
  isCreate,
  allowRecurring,
  price,
  primaryPrices,
  copy,
  ticketTypes,
  otherOrderBumps,
  onCancel,
  onDelete,
  isSaving,
}) {
  const { values, setFieldValue, errors, touched, status, dirty } = useFormikContext();
  const [isDescriptionLoading, setIsDescriptionLoading] = useState(false);

  // Save stays live whatever the fields hold — the app-wide row does not grey it
  // out for validation, and an incomplete bump is refused by the server with a
  // message. The one real block is a description image still uploading, which
  // would otherwise be saved half-attached.
  const isBlocked = isDescriptionLoading || isSaving;

  const renderConfigFields = fields =>
    fields
      .filter(f => !getIsFieldHidden(f, values))
      .map((f, index) => (
        <Field
          key={f.name || `${f.type}-${index}`}
          field={f}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
      ));

  return (
    <FormikForm>
      <UnsavedChangesReporter />
      {renderConfigFields(identityFields)}

      {/* The same single price form as /dashboard/checkouts/new, minus "Add
          price" — a bump carries exactly one. singlePrice drops Label and
          Description, which only mean something when a buyer picks between
          prices, except Label on a PWYW price, which heads its amount box on
          the card; hideBadge
          drops Highlight, which this form renders itself under the short
          description.

          On a saved price lockBilling hides the Billing/Schedule/Pricing radios,
          which is correct — validatePriceIdentityLock rejects changing any of
          them once a price has an id. The type is named in the row header
          instead. Payment plans are never offered: the iteration cap lives on
          the subscription schedule phase, so a bump riding the primary's
          subscription cannot carry its own. Pay what you want is one-time only:
          the API refuses it on a recurring bump. */}
      <PriceForm
        fieldPrefix="prices[0]"
        price={price ?? {}}
        singlePrice
        hideBadge
        currencyAsSuffix
        currencyTooltip={currencyTooltip(copy)}
        lockBilling={!isCreate}
        allowRecurring={allowRecurring}
        allowPaymentPlan={false}
        allowRecurringPayWhatYouWant={false}
      />

      <div className="my-4 border-t border-gray-100 dark:border-border" />
      {renderConfigFields([
        // Above the description on the card, so above it here.
        {
          name: 'features',
          label: 'Features',
          type: 'custom',
          tooltip:
            'A short list of what the customer gets, shown above the description. Each line can lead with an icon.',
          renderField: () => <FeaturesField />,
        },
        {
          name: 'description',
          label: 'Description',
          type: 'custom',
          // The editor reports its own in-flight image uploads; Save waits on
          // them so a half-uploaded image cannot be saved into the description.
          renderField: () => <DeferredDescription onLoading={setIsDescriptionLoading} />,
        },
        // Placement state like Preselect, so it rides Save and reaches the same
        // patch — but it sits here rather than in Options because it is about
        // this description, and the seller decides it while writing one.
        {
          name: 'shortenDescription',
          label: 'Show more/less',
          type: 'checkbox',
          tooltip: 'Show the first line only, with a Show more link the customer can expand.',
        },
        // Under the description on the card, so under it here.
        {
          name: 'calloutText',
          label: 'Call out',
          type: 'custom',
          tooltip: 'A box under the description.',
          renderField: () => (
            <HighlightField
              name="calloutText"
              iconName="calloutIcon"
              multiline
              pickerLabel="Call out icon"
              ariaLabel="Call out"
              placeholder="e.g. Offer ends Sunday"
            />
          ),
        },
      ])}

      {/* Every section is on screen in both modes, but they reach the product by
          different routes. Editing, images and downloads commit on drop, reorder
          and delete — they are attach/detach calls, not part of Save. Creating,
          there is no id to attach to yet: the upload itself needs no product, so
          the draft editors collect file ids and the caller links them once the
          create returns. Stock and SKU are plain scalars and ride Save either way.

          Inlined under subheadings rather than collapsed: this whole row is
          already an accordion item, and an accordion inside an accordion is a
          poor place to put a form. */}
      <div className="my-4 border-t border-gray-100 dark:border-border" />
      {/* A bump card has room for one thumbnail, so the gallery stops at
          one — the server rejects a second (ProductService.attachMedia). */}
      <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-foreground">
        Product image
      </h4>
      {isCreate ? (
        <DraftMediaForm />
      ) : (
        <ProductMediaForm productId={productId} maxItems={BUMP_IMAGE_LIMIT} />
      )}

      <div className="my-4 border-t border-gray-100 dark:border-border" />
      <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-foreground">
        File downloads
      </h4>
      {isCreate ? <DraftFilesForm /> : <ProductFilesForm productId={productId} />}

      <div className="my-4 border-t border-gray-100 dark:border-border" />
      {renderConfigFields(cardCopyFields)}

      {renderConfigFields([
        {
          type: 'disclosure',
          label: 'Product options',
          name: 'product-options',
          fields: additionalFields({
            isCreate,
            primaryPrices,
            copy,
            ticketTypes,
            otherOrderBumps,
            values,
            setFieldValue,
          }),
        },
      ])}

      {status && <div className="mt-2 text-sm text-red-600 dark:text-red-400">{status}</div>}

      {/* The app-wide save row. Save submits the form rather than calling a
          handler, so an empty required field raises the browser's own prompt
          the way every other editor here does. */}
      <div className="mt-4 flex items-center justify-between gap-2">
        {/* Destructive actions sit away from Save, as they do across the app. */}
        {onDelete ? (
          <Button
            type="button"
            appearance="link"
            className="text-red-600 dark:text-red-400"
            onClick={onDelete}
          >
            Delete
          </Button>
        ) : (
          <span />
        )}

        <FormSubmit align="end" hasMargin={false}>
          <FormSubmit.State visible={dirty} />
          <FormSubmit.Cancel onClick={onCancel} />
          <FormSubmit.Button disabled={isBlocked}>Save</FormSubmit.Button>
        </FormSubmit>
      </div>
    </FormikForm>
  );
}

/**
 * One form for creating and editing an order bump product.
 *
 * Given a `productId` it edits that product and saves itself. Given `onCreate`
 * instead, it builds the create payload and hands it back — the caller creates
 * the product and attaches it, because attaching is a page concern. Images and
 * downloads uploaded before that product existed ride along as a second argument
 * of file ids for the caller to attach once it has an id.
 *
 * The two modes differ only in what a product without an id cannot have:
 * creation is the sole moment billing type can be chosen (see PriceForm's
 * lockBilling above), and images, file downloads and inventory need something
 * to attach to.
 *
 * Everything here belongs to the PRODUCT, so a saved change reaches every page
 * offering the bump. The page-scoped half is `enabled` and the placement order,
 * both on the row itself.
 */
export default function BumpProductForm({
  productId,
  currency,
  allowRecurring,
  // The page product's prices — what a conditional-logic rule points at.
  primaryPrices = [],
  copy = ORDER_BUMP_COPY,
  // The event's ticket types, when the page is an event: what a
  // conditional-logic rule points at there instead of the prices.
  ticketTypes,
  allTicketTypes = ticketTypes,
  // The page's other bumps, `{ productId, title }`, which a rule can watch too.
  otherOrderBumps = [],
  onCreate,
  onCancel,
  onDelete,
  preselected = false,
  hidden = false,
  allowQuantity = false,
  shortenDescription = false,
  showHideLogic,
  // One call with only what changed, on Save. The placement settings share an
  // endpoint that takes a partial, so they travel together rather than as a
  // request each.
  onPlacementSave,
  isSaving = false,
}) {
  const { seller } = useAuth();
  const { product, updateProduct } = useProduct(productId);

  const isCreate = !productId;
  // The product is fetched when the row opens, so rendering nothing meanwhile
  // left the row visibly empty for a round trip. A skeleton of roughly the
  // form's shape lets it open at once and fill in.
  if (!isCreate && !product) {
    return (
      <div className="space-y-4 py-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const defaults = getProductFormValues(isCreate ? {} : product, seller ?? {});
  // Disabled tickets too, so the names saved with a rule cover every ticket in it.
  const ruleLists = { prices: primaryPrices, ticketTypes: allTicketTypes, otherOrderBumps };
  const price = isCreate ? undefined : product.prices?.[0];

  // getProductFormValues covers the product form's own fields; these are the
  // bump form's additions. headingText, callToActionText and shortDescription are bump-only;
  // description is a product field the details form edits through its OWN
  // Formik (ProductDescriptionForm), so it is absent from the shared seed and
  // has to be named here; stock/sku moved in from ProductInventoryForm so one
  // Save covers them.
  const seed = source => ({
    ...getProductFormValues(source, seller ?? {}),
    headingText: source?.headingText ?? '',
    callToActionText: source?.callToActionText ?? '',
    shortDescription: source?.shortDescription ?? '',
    features: seedFeatures(source?.features),
    calloutText: source?.calloutText ?? '',
    calloutIcon: source?.calloutIcon ?? null,
    description: source?.description ?? '',
    hasUnlimitedStock: source?.hasUnlimitedStock ?? false,
    preselected,
    hidden,
    allowQuantity,
    shortenDescription,
    // Placement state, like the two above: it arrives as a prop from the row's
    // entry, never off the product.
    showHideLogic: showHideLogic ?? NO_SHOW_HIDE_LOGIC,
    stock: source?.stock ?? '',
    sku: source?.sku ?? '',
  });

  const initialValues = isCreate
    ? {
        ...defaults,
        title: '',
        headingText: '',
        callToActionText: '',
        shortDescription: '',
        features: seedFeatures([]),
        calloutText: '',
        calloutIcon: null,
        description: '',
        // Uploaded before the product exists; attached by the caller after create.
        mediaFileIds: [],
        fileIds: [],
        // Most bumps are not stock-tracked, so a new one starts unlimited and
        // the seller opts into counting rather than out of it.
        hasUnlimitedStock: true,
        stock: '',
        sku: '',
        preselected: false,
        hidden: false,
        allowQuantity: false,
        shortenDescription: false,
        showHideLogic: NO_SHOW_HIDE_LOGIC,
        // A bump must share the page product's currency — there is nothing to pick.
        currency,
        prices: [{ ...defaults.prices[0], currency }],
      }
    : seed(product);

  return (
    <Formik
      // Keyed rather than reinitialized: a background refetch of the standalone
      // product query must not wipe edits in progress.
      key={productId ?? 'new'}
      initialValues={initialValues}
      onSubmit={async (rawValues, { resetForm, setStatus }) => {
        // Ran ahead of the old Save handler; now that Save is a plain submit it
        // belongs here, before the payload is built either way.
        const values = { ...rawValues, prices: rawValues.prices.map(normalizeRevealFlags) };

        // Placement state rides Save rather than committing as it is edited, so
        // the preview does not move while the seller is still deciding — and it
        // goes to the PLACEMENT endpoint, not the product one below.
        const nextPlacement = {
          preselected: values.preselected === true,
          hidden: values.hidden === true,
          allowQuantity: values.allowQuantity === true,
          shortenDescription: values.shortenDescription === true,
          showHideLogic: normalizeShowHideLogic(values.showHideLogic, ruleLists),
        };
        const savedPlacement = {
          preselected: preselected === true,
          hidden: hidden === true,
          allowQuantity: allowQuantity === true,
          shortenDescription: shortenDescription === true,
          showHideLogic: normalizeShowHideLogic(showHideLogic, ruleLists),
        };
        const patch = placementPatch(nextPlacement, savedPlacement);

        if (isCreate) {
          onCreate(
            buildNewBumpProduct({
              title: values.title,
              headingText: values.headingText,
              callToActionText: values.callToActionText,
              shortDescription: values.shortDescription,
              features: values.features,
              calloutText: values.calloutText,
              calloutIcon: values.calloutIcon,
              description: values.description,
              currency: values.currency,
              price: values.prices[0],
              hasUnlimitedStock: values.hasUnlimitedStock,
              stock: values.stock,
              sku: values.sku,
            }),
            { media: values.mediaFileIds, files: values.fileIds },
            patch,
          );
          return;
        }

        const { data, error } = await updateProduct.mutateAsync({
          title: values.title.trim(),
          // `null`, not `undefined`: JSON.stringify drops an undefined key, so
          // the server would never see the field and would keep the old copy —
          // which is what made an emptied one impossible to clear. Null still
          // avoids storing a blank string; the server sets the path to it.
          headingText: values.headingText?.trim() || null,
          callToActionText: values.callToActionText?.trim() || null,
          shortDescription: values.shortDescription?.trim() || null,
          features: toFeaturesPayload(values.features),
          calloutText: values.calloutText?.trim() || null,
          calloutIcon: values.calloutIcon || null,
          description: values.description,
          currency: values.currency,
          hasUnlimitedStock: values.hasUnlimitedStock,
          stock: values.stock,
          sku: values.sku,
          // `_id` rides along in the seed spread — without it the save mints a
          // new price and orphans the Stripe one.
          prices: [draftToPricePayload(values.prices[0], values.currency)],
        });

        if (error) {
          setStatus(error.message || `Unable to save the ${copy.noun}`);
          return;
        }

        if (Object.keys(patch).length) onPlacementSave?.(patch);

        // `seed` reads placement off the props, which are still the pre-save
        // entry until that response lands — so the just-saved values are put
        // back explicitly or the fields would visibly revert.
        resetForm({ values: { ...seed(data ?? product), ...nextPlacement } });
      }}
    >
      <FormBody
        productId={productId}
        isCreate={isCreate}
        allowRecurring={allowRecurring}
        price={price}
        primaryPrices={primaryPrices}
        copy={copy}
        ticketTypes={ticketTypes}
        otherOrderBumps={otherOrderBumps}
        onCancel={onCancel}
        onDelete={onDelete}
        isSaving={isSaving}
      />
    </Formik>
  );
}
